/*
==================================================
CATEGORY CAROUSEL DESKTOP

Функция:
desktop-версия карусели категорий.
Содержит закрепленную верхнюю панель,
горизонтальную карусель root categories
и отдельный блок подкатегорий.

Родитель:
category-carousel-shell.tsx

Дочерние элементы:
нет

Использует:
- getCountValue

Примечания:
desktop работает только по клику.
Автозакрытие по клику вне панели и scroll включается только на desktop.
hover не открывает подкатегории.
==================================================
*/

"use client"

import { useEffect, useRef } from "react"
import { clx } from "@medusajs/ui"
import { getCountValue } from "./index"

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)"

const isDesktopViewport = () => {
  if (typeof window === "undefined") return false

  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches
}

type DisplayCategoryItem = {
  id: string
  name: string
  handle?: string | null
  products?: { id: string }[] | null
  children: DisplayCategoryItem[]
}

type CategoryCarouselDesktopProps = {
  rootItems: DisplayCategoryItem[]
  selectedCategory?: string
  expandedCategoryHandle: string | null
  setExpandedCategoryHandle: React.Dispatch<React.SetStateAction<string | null>>
  visibleSubcategories: DisplayCategoryItem[]
  desktopScrollRef: React.RefObject<HTMLDivElement | null>
  onApplyCategory: (handle?: string | null) => void
  onDesktopSubcategorySelect: (handle?: string | null) => void
  onRootCategoryClick: (item: DisplayCategoryItem) => void
  onDesktopRootHover: (item: DisplayCategoryItem) => void
  onDesktopScroll: (direction: "left" | "right") => void
}

export default function CategoryCarouselDesktop({
  rootItems,
  selectedCategory,
  expandedCategoryHandle,
  setExpandedCategoryHandle,
  visibleSubcategories,
  desktopScrollRef,
  onDesktopSubcategorySelect,
  onRootCategoryClick,
  onDesktopScroll,
}: CategoryCarouselDesktopProps) {
  const desktopPanelRef = useRef<HTMLDivElement | null>(null)
  const subcategoriesCollapseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const clearSubcategoriesCollapse = () => {
    if (!subcategoriesCollapseTimeoutRef.current) return

    clearTimeout(subcategoriesCollapseTimeoutRef.current)
    subcategoriesCollapseTimeoutRef.current = null
  }

  const scheduleSubcategoriesCollapse = (categoryHandle: string | null) => {
    clearSubcategoriesCollapse()

    if (!isDesktopViewport()) return

    subcategoriesCollapseTimeoutRef.current = setTimeout(() => {
      setExpandedCategoryHandle((currentHandle) =>
        currentHandle === categoryHandle ? null : currentHandle
      )

      subcategoriesCollapseTimeoutRef.current = null
    }, 1400)
  }

  const centerDesktopCard = (button: HTMLButtonElement | null) => {
    const scroller = desktopScrollRef.current
    if (!scroller || !button) return

    const cardLeft = button.offsetLeft
    const cardWidth = button.offsetWidth
    const viewportWidth = scroller.clientWidth

    const leftOffset = Math.max(
      cardLeft - Math.max((viewportWidth - cardWidth) / 2, 24),
      0
    )

    scroller.scrollTo({
      left: leftOffset,
      behavior: "smooth",
    })
  }

  const expandedCategory =
    rootItems.find((item) => item.handle === expandedCategoryHandle) || null

  const handleRootCategoryClick = (item: DisplayCategoryItem) => {
    clearSubcategoriesCollapse()
    onRootCategoryClick(item)
  }

  const handleSubcategorySelect = (handle?: string | null) => {
    const currentExpandedCategoryHandle = expandedCategoryHandle

    onDesktopSubcategorySelect(handle)

    if (!currentExpandedCategoryHandle) return

    setExpandedCategoryHandle(currentExpandedCategoryHandle)
    scheduleSubcategoriesCollapse(currentExpandedCategoryHandle)
  }

  useEffect(() => {
    return () => {
      clearSubcategoriesCollapse()
    }
  }, [])

  useEffect(() => {
    if (!expandedCategoryHandle) return
    if (!isDesktopViewport()) return

    const handleOutsideClick = (event: MouseEvent) => {
      if (!isDesktopViewport()) return

      const target = event.target as Node | null

      if (target && desktopPanelRef.current?.contains(target)) return

      clearSubcategoriesCollapse()
      setExpandedCategoryHandle(null)
    }

    const handleWindowScroll = () => {
      if (!isDesktopViewport()) return

      clearSubcategoriesCollapse()
      setExpandedCategoryHandle(null)
    }

    document.addEventListener("mousedown", handleOutsideClick)
    window.addEventListener("scroll", handleWindowScroll, { passive: true })

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      window.removeEventListener("scroll", handleWindowScroll)
    }
  }, [expandedCategoryHandle, setExpandedCategoryHandle])

  return (
    <div
      ref={desktopPanelRef}
      className="
       hidden md:block
       bg-white/95 backdrop-blur-md
       pt-1 pb-1
       px-5 lg:px-2
      "
    >
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-black uppercase tracking-[0.18em] text-[#1b2b46]/70">
            Categories
          </div>

          <div className="mt-1 h-[3px] w-28 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.28)]" />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onDesktopScroll("left")}
            className="
              flex h-10 w-10 items-center justify-center
              rounded-full border border-slate-200
              bg-white/95 text-[#1b2b46]
              shadow-[0_8px_18px_rgba(15,23,42,0.10)]
              transition-all duration-300
              hover:-translate-y-0.5 hover:shadow-[0_14px_24px_rgba(15,23,42,0.14)]
              active:scale-[0.90]
            "
            aria-label="Scroll categories left"
          >
            ‹
          </button>

          <button
            type="button"
            onClick={() => onDesktopScroll("right")}
            className="
              flex h-10 w-10 items-center justify-center
              rounded-full border border-slate-200
              bg-white/95 text-[#1b2b46]
              shadow-[0_8px_18px_rgba(15,23,42,0.10)]
              transition-all duration-300
              hover:-translate-y-0.5 hover:shadow-[0_14px_24px_rgba(15,23,42,0.14)]
              active:scale-[0.90]
            "
            aria-label="Scroll categories right"
          >
            ›
          </button>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-[#f5f5f5] to-transparent sm:from-white" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#f5f5f5] to-transparent sm:from-white" />

        <div
          ref={desktopScrollRef}
          className="
            no-scrollbar flex gap-3 overflow-x-auto
            px-0 pb-3 pt-1
            snap-x snap-mandatory scroll-smooth
          "
        >
          {rootItems.map((item) => {
            const isActuallyActive =
              item.handle === ""
                ? !selectedCategory
                : item.handle === selectedCategory ||
                  item.children.some((child) => child.handle === selectedCategory)

            const hasChildren = item.children.length > 0
            const isExpanded = expandedCategoryHandle === item.handle
            const count = item.handle === "" ? null : getCountValue(item)

            return (
              <button
                key={item.id}
                type="button"
                onClick={(event) => {
                  centerDesktopCard(event.currentTarget)
                  handleRootCategoryClick(item)
                }}
                data-active-desktop-category={isActuallyActive ? "true" : "false"}
                className={clx(
                  `
                    group relative shrink-0 snap-start overflow-hidden
                    rounded-[26px] border text-left
                    transition-all duration-300 active:scale-[0.985]
                    min-h-[88px] w-[300px] px-4 py-4
                  `,
                  isActuallyActive
                    ? `
                      border-white/80
                      bg-gradient-to-b from-white via-white to-slate-50
                      shadow-[0_16px_32px_rgba(15,23,42,0.14),inset_0_1px_0_rgba(255,255,255,0.95)]
                      -translate-y-[2px]
                    `
                    : isExpanded
                    ? `
                      border-slate-200
                      bg-gradient-to-b from-white via-white to-slate-50
                      shadow-[0_12px_24px_rgba(15,23,42,0.10),inset_0_1px_0_rgba(255,255,255,0.95)]
                      -translate-y-[1px]
                    `
                    : `
                      border-slate-200
                      bg-gradient-to-b from-slate-50 via-white to-slate-100
                      shadow-[0_10px_22px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.92)]
                      hover:-translate-y-[2px]
                      hover:shadow-[0_16px_30px_rgba(15,23,42,0.12)]
                    `
                )}
                aria-pressed={isActuallyActive || isExpanded}
              >
                <div
                  className={clx(
                    "pointer-events-none absolute inset-0 rounded-[26px]",
                    isActuallyActive || isExpanded
                      ? "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.95),transparent_42%)]"
                      : "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.75),transparent_40%)]"
                  )}
                />

                <div
                  className={clx(
                    `
                      pointer-events-none absolute left-4 right-4 bottom-2
                      h-[4px] rounded-full transition-all duration-300
                    `,
                    isActuallyActive
                      ? "bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_14px_rgba(249,115,22,0.35)]"
                      : "bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200"
                  )}
                />

                <div className="relative z-[1] flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className={clx(
                        "text-[14px] font-extrabold uppercase tracking-[0.04em] leading-[1.15]",
                        isActuallyActive ? "text-[#10233d]" : "text-[#1b2b46]"
                      )}
                    >
                      {item.name}
                    </div>

                    <div
                      className={clx(
                        "mt-2 text-xs font-medium",
                        isActuallyActive ? "text-slate-600" : "text-slate-500"
                      )}
                    >
                      {item.handle === ""
                        ? "Show the full product feed"
                        : hasChildren
                        ? "Click to open subcategories"
                        : "Click to focus this category"}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center self-start mt-0">
                    {hasChildren ? (
                      <div
                        className={clx(
                          `
                            inline-flex min-w-[54px] items-center justify-center
                            rounded-full border px-3 py-1.5
                            shadow-[0_8px_16px_rgba(15,23,42,0.12)]
                            transition-all duration-300
                          `,
                          isActuallyActive
                            ? "border-[#1e3a5f]/15 bg-gradient-to-b from-[#183b63] to-[#0f2742]"
                            : isExpanded
                            ? "border-slate-200 bg-gradient-to-b from-[#24476f] to-[#12304f]"
                            : "border-slate-200 bg-white"
                        )}
                      >
                        <span
                          className={clx(
                            "block text-[18px] leading-none font-black transition-transform duration-300",
                            isExpanded && "rotate-180",
                            isActuallyActive || isExpanded
                              ? "text-white"
                              : "text-[#1b2b46]"
                          )}
                        >
                          ▾
                        </span>
                      </div>
                    ) : (
                      <div
                        className={clx(
                          `
                            inline-flex shrink-0 items-center justify-center
                            rounded-full px-3 py-1
                            text-[11px] font-black uppercase tracking-[0.12em]
                          `,
                          isActuallyActive
                            ? "border border-[#1e3a5f]/15 bg-gradient-to-b from-[#183b63] to-[#0f2742] text-white shadow-[0_8px_16px_rgba(15,23,42,0.22)]"
                            : "border border-slate-200 bg-white text-[#1b2b46] shadow-[0_6px_14px_rgba(15,23,42,0.08)]"
                        )}
                      >
                        {item.handle === "" ? "All" : count}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {visibleSubcategories.length > 0 && (
        <div
          className="
            mt-2 rounded-[26px] border border-white/80
            bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.96)_100%)]
            px-4 py-4
            shadow-[0_16px_32px_rgba(15,23,42,0.10),0_6px_14px_rgba(15,23,42,0.06)]
          "
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[14px] font-black uppercase tracking-[0.12em] text-[#1b2b46]/80">
                {expandedCategory?.name || "Category"}
              </div>
              <div className="mt-1 h-[3px] w-24 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.20)]" />
            </div>

            <button
              type="button"
              onClick={() => {
                handleSubcategorySelect(expandedCategoryHandle || undefined)
              }}
              className={clx(
                `
                  inline-flex items-center justify-center rounded-full
                  border px-3 py-1.5
                  text-[11px] font-black uppercase tracking-[0.12em]
                  shadow-[0_6px_14px_rgba(15,23,42,0.08)]
                  transition-all duration-300
                  hover:-translate-y-0.5 hover:shadow-[0_10px_18px_rgba(15,23,42,0.10)]
                  active:scale-[0.98]
                `,
                selectedCategory === expandedCategoryHandle
                  ? "border-white/80 bg-gradient-to-b from-[#183b63] to-[#0f2742] text-white shadow-[0_12px_22px_rgba(15,23,42,0.18)]"
                  : "border-slate-200 bg-white text-[#1b2b46]"
              )}
              aria-pressed={selectedCategory === expandedCategoryHandle}
            >
              View all
            </button>
          </div>

          <div className="flex flex-wrap gap-3">
            {visibleSubcategories.map((subcat) => {
              const isSubActive = subcat.handle === selectedCategory
              const subCount = getCountValue(subcat)

              return (
                <button
                  key={subcat.id}
                  type="button"
                  onClick={() => {
                    handleSubcategorySelect(subcat.handle)
                  }}
                  className={clx(
                    `
                      inline-flex items-center gap-2 rounded-full border px-4 py-2.5
                      text-left text-[13px] font-bold transition-all duration-300
                      active:scale-[0.98]
                    `,
                    isSubActive
                      ? "border-white/80 bg-gradient-to-b from-[#183b63] to-[#0f2742] text-white shadow-[0_12px_22px_rgba(15,23,42,0.18)]"
                      : "border-slate-200 bg-white text-[#1b2b46] shadow-[0_8px_16px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 hover:shadow-[0_12px_22px_rgba(15,23,42,0.12)]"
                  )}
                  aria-pressed={isSubActive}
                >
                  <span>{subcat.name}</span>
                  <span
                    className={clx(
                      "rounded-full px-2 py-0.5 text-[10px] font-black",
                      isSubActive
                        ? "bg-white/15 text-white"
                        : "bg-slate-100 text-[#1b2b46]"
                    )}
                  >
                    {subCount}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
